//src/components/Login.js

import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useSocket } from '../SocketContext';

const Login = () => {
    const [formData, setFormData] = useState({
        identifier: '', 
        password: '',
        // Nouvel état pour le code 2FA
        otpToken: '',
    });
    const [message, setMessage] = useState('');
    
    // 🛑 Nouvel état pour contrôler l'affichage du formulaire 2FA
    const [show2faForm, setShow2faForm] = useState(false); 
    
    // Assurez-vous que useSocket est disponible
    const { connectSocket = () => {} } = useSocket() || {}; 
    
    const navigate = useNavigate();
    const location = useLocation(); 
    
    // Déstructuration du nouvel état
    const { identifier, password, otpToken } = formData;

    const onChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const completeLogin = (token, user) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        connectSocket(); 
        
        const redirectTo = location.state?.from || '/dashboard';
        navigate(redirectTo, { replace: true });
    }

    // ----------------------------------------------------
    // 🔑 FONCTION 1 : TENTATIVE DE CONNEXION INITIALE
    // ----------------------------------------------------
    const onSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        
        const cleanedIdentifier = identifier.trim(); 
        const cleanedPassword = password.trim(); 
        
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json'
                }
            };
            
            const body = JSON.stringify({ 
                identifier: cleanedIdentifier, 
                password: cleanedPassword 
            });
            
            const res = await axios.post('https://api.blackroq.pro/api/auth/login', body, config);
            
            // 🛑 Vérification 2FA 🛑
            if (res.data.requires2fa) {
                // Le backend demande le code 2FA
                setMessage(res.data.message);
                setShow2faForm(true); // Affiche le nouveau formulaire
                return; // Arrête le processus de connexion ici
            }

            // Connexion standard réussie (pas de 2FA ou 2FA désactivé)
            completeLogin(res.data.token, res.data.user);

        } catch (err) {
            console.error('Erreur de la requête:', err);
            
            if (err.response) {
                setMessage(err.response.data.message || err.response.data.msg || 'Identifiant ou mot de passe incorrect.'); 
            } else {
                setMessage('Une erreur de connexion est survenue.');
            }
        }
    };


    // ----------------------------------------------------
    // 🔑 FONCTION 2 : VÉRIFICATION DU CODE 2FA
    // ----------------------------------------------------
    const onSubmit2fa = async (e) => {
        e.preventDefault();
        setMessage('');

        try {
            const config = {
                headers: { 'Content-Type': 'application/json' }
            };

            const body = JSON.stringify({ 
                identifier: identifier.trim(), 
                token: otpToken.trim() // Le code à 6 chiffres
            });
            
            // Envoi à la nouvelle route de vérification 2FA
            const res = await axios.post('https://api.blackroq.pro/api/auth/2fa/login-verify', body, config);
            
            // Connexion réussie après vérification 2FA
            completeLogin(res.data.token, res.data.user);

        } catch (err) {
            console.error('Erreur de vérification 2FA:', err);
            if (err.response) {
                setMessage(err.response.data.msg || 'Code 2FA invalide. Veuillez réessayer.');
            } else {
                setMessage('Erreur de communication avec le serveur lors de la vérification 2FA.');
            }
        }
    };


    const handleGoogleLogin = () => {
        window.location.href = 'https://api.blackroq.pro/api/auth/google';
    };

    // ----------------------------------------------------
    // 🧱 RENDU DU COMPOSANT
    // ----------------------------------------------------
    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-6">
                    <div className="card shadow p-4">
                        <h1 className="text-center mb-4">Se connecter</h1>
                        {message && <div className={`alert ${show2faForm ? 'alert-warning' : 'alert-danger'}`}>{message}</div>}

                        {/* 🛑 AFFICHAGE CONDITIONNEL DU FORMULAIRE 2FA 🛑 */}
                        {show2faForm ? (
                            // FORMULAIRE 2FA
                            <form onSubmit={e => onSubmit2fa(e)}>
                                <div className="mb-2"> 
                                    <label htmlFor="otpToken" className="form-label">Code d'authentification à deux facteurs</label>
                                    <input
                                        type="text"
                                        id="otpToken"
                                        className="form-control"
                                        placeholder="Entrez le code à 6 chiffres"
                                        name="otpToken"
                                        value={otpToken}
                                        onChange={e => onChange(e)}
                                        maxLength="6"
                                        required
                                    />
                                </div>
                                <div className="d-grid gap-2 mt-3">
                                    <button type="submit" className="btn btn-success">Vérifier et Se connecter</button>
                                </div>
                                <p className="mt-3 text-center">
                                    <button type="button" className="btn btn-link" onClick={() => setShow2faForm(false)}>Retour à la connexion</button>
                                </p>
                            </form>
                        ) : (
                            // FORMULAIRE DE CONNEXION NORMAL
                            <form onSubmit={e => onSubmit(e)}>
                                <div className="mb-2"> 
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Adresse e-mail ou Numéro de téléphone"
                                        name="identifier"
                                        value={identifier}
                                        onChange={e => onChange(e)}
                                        required
                                    />
                                </div>
                                <div className="mb-2">
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder="Mot de passe"
                                        name="password"
                                        value={password}
                                        onChange={e => onChange(e)}
                                        required
                                    />
                                </div>
                                <div className="d-grid gap-2 mt-3">
                                    <button type="submit" className="btn btn-primary">Se connecter</button>
                                    <button type="button" className="btn btn-danger" onClick={handleGoogleLogin}>
                                        <FontAwesomeIcon icon={faGoogle} className="me-2" /> Se connecter avec Google
                                    </button>
                                </div>
                            </form>
                        )}
                        {/* Les liens restent inchangés */}
                        <p className="mt-3 text-center">
                            <Link to="/forgot-password">Mot de passe oublié ?</Link>
                        </p>
                        <p className="mt-3 text-center">
                            Pas encore de compte ? <Link to="/register">S'inscrire</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;